/**
 * Sanitizes markdown content to fix common formatting issues
 * @param {string} markdown - The markdown string to sanitize
 * @returns {string} - Sanitized markdown string
 */
export const sanitizeMarkdown = (markdown) => {
    if (!markdown) return '';

    let sanitized = markdown;

    // First, convert literal \n strings to actual newlines if they exist
    // This handles cases where backend sends escaped newlines
    if (sanitized.includes('\\n')) {
        sanitized = sanitized.replace(/\\n/g, '\n');
    }

    // Transform the payment cycles table into a simpler format
    // Match the specific table pattern and convert it (including the leading pipe)
    const tablePattern = /\|\s*Pay Cycle \| Debit Date \|[|\s::-]+\*\*Weekly\*\* \| ([^|]+) \|[|\s]+\*\*Fortnightly\*\* \| ([^|]+) \|[|\s]+\*\*Monthly\*\* \| ([^.]+\.)/;
    const tableMatch = sanitized.match(tablePattern);
    
    if (tableMatch) {
        const [fullMatch, weeklyText, fortnightlyText, monthlyText] = tableMatch;
        const replacement = `\n\n**Pay Cycle | Debit Date**\n\n**Weekly:** ${weeklyText.trim()}\n\n**Fortnightly:** ${fortnightlyText.trim()}\n\n**Monthly:** ${monthlyText.trim()}`;
        sanitized = sanitized.replace(fullMatch, replacement);
    }
    
    // Now convert remaining double spaces to newlines (for non-table content)
    sanitized = sanitized.replace(/  +/g, '\n');

    // Ensure list items with bold numbered sections are on new lines
    // Match patterns like "* 4.2" or "* **4.2" at the start of what should be a new line
    sanitized = sanitized.replace(/([^\n])(\* \*?\*?\d+\.\d+)/g, '$1\n$2');
    
    // Fix markdown headings that might be stuck together
    sanitized = sanitized.replace(/([^\n])(#{1,6} )/g, '$1\n\n$2');
    
    // Fix horizontal rules that might be stuck to text
    // BUT don't break table alignment rows (which use :--- or ---:)
    sanitized = sanitized.replace(/([^\n:])(\s*---(?![-:]))/g, '$1\n\n$2');
    sanitized = sanitized.replace(/(----)([^\n-:])/g, '$1\n\n$2');

    // Fix nested list indentation: replace 4+ spaces before * with 2 spaces
    // This prevents nested lists from being interpreted as code blocks
    sanitized = sanitized.replace(/\n {4,}(\*|[a-z]\))/g, '\n  $1');
    
    // Ensure nested list items (a), b), c) that might be on same line are moved to new lines
    sanitized = sanitized.replace(/([.!?:])(\s*)([a-z]\))/g, '$1\n  $3');

    // Ensure consistent spacing after list items with sub-items
    // Add blank line before continuation text if needed
    sanitized = sanitized.replace(/(\n {2}\*.*)\n(\n {2}by providing)/g, '$1\n$2');

    // Normalize multiple consecutive blank lines to maximum of 2
    sanitized = sanitized.replace(/\n{3,}/g, '\n\n');

    // Ensure proper spacing around horizontal rules
    sanitized = sanitized.replace(/([^\n])\n---/g, '$1\n\n---');
    sanitized = sanitized.replace(/---\n([^\n])/g, '---\n\n$1');

    return sanitized;
};
